(() => {
  if (customElements.get('omri-search-drawer')) return;

  class OmriSearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.isDrawerOpen = false;
      this.lastFocused = null;
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onViewportResize = this.onViewportResize.bind(this);
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      if (this.dataset.omriSearchBound === 'true') return;
      this.dataset.omriSearchBound = 'true';

      this.trigger = this.querySelector('[data-omri-search-open]');
      this.panel = this.querySelector('[data-omri-search-panel]');
      this.input = this.querySelector('input[type="search"]');
      this.predictive = this.querySelector('predictive-search');
      this.closeControls = this.querySelectorAll('[data-omri-search-close]');
      this.backdrop = document.querySelector('[data-omri-search-global-backdrop]');

      if (!this.backdrop) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'omri-search-global-backdrop';
        this.backdrop.dataset.omriSearchGlobalBackdrop = '';
        this.backdrop.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.backdrop);
      }

      if (!this.trigger || !this.panel || !this.input) return;

      this.trigger.addEventListener('click', () => this.open());
      this.closeControls.forEach((control) => {
        control.addEventListener('click', () => this.close(true));
      });
      this.input.addEventListener('input', this.onInput);

      document.addEventListener('keydown', this.onDocumentKeydown);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportResize);
        window.visualViewport.addEventListener('scroll', this.onViewportResize);
      }

      this.panel.inert = true;
      this.updateViewportHeight();
      this.updateQueryState();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.input?.removeEventListener('input', this.onInput);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onViewportResize);
        window.visualViewport.removeEventListener('scroll', this.onViewportResize);
      }
    }

    open() {
      if (this.isDrawerOpen) return;

      this.isDrawerOpen = true;
      this.lastFocused = document.activeElement;
      this.updateViewportHeight();

      this.classList.add('is-open');
      this.backdrop?.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.inert = false;
      document.body.classList.add('omri-search-open');

      // Desktop: focus after the slide. Mobile: intentionally leave focus on
      // the search trigger so the virtual keyboard does not appear immediately.
      if (window.matchMedia('(min-width: 750px)').matches) {
        window.setTimeout(() => {
          if (!this.isDrawerOpen) return;
          this.input.focus({ preventScroll: true });
        }, this.prefersReducedMotion() ? 0 : 620);
      }
    }

    close(restoreFocus = true) {
      if (!this.isDrawerOpen) return;

      this.isDrawerOpen = false;
      this.classList.remove('is-open');
      this.backdrop?.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.inert = true;
      document.body.classList.remove('omri-search-open');

      // Reopen on the editorial suggestions, not on stale predictive results.
      if (this.predictive && typeof this.predictive.close === 'function') {
        this.predictive.close(true);
      } else {
        this.input.value = '';
      }
      this.updateQueryState();

      if (restoreFocus) {
        const focusTarget = this.trigger || this.lastFocused;
        if (focusTarget && typeof focusTarget.focus === 'function') {
          window.setTimeout(() => {
            focusTarget.focus({ preventScroll: true });
          }, this.prefersReducedMotion() ? 0 : 20);
        }
      }
    }

    onInput() {
      this.updateQueryState();
    }

    updateQueryState() {
      const hasQuery = Boolean(this.input?.value?.trim());
      this.classList.toggle('has-query', hasQuery);
    }

    onDocumentKeydown(event) {
      if (!this.isDrawerOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = this.getFocusableElements();
      if (!focusables.length) {
        event.preventDefault();
        this.panel.focus({ preventScroll: true });
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements() {
      return Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);
    }

    onViewportResize() {
      if (this.isDrawerOpen) this.updateViewportHeight();
    }

    updateViewportHeight() {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;

      if (height) this.style.setProperty('--omri-search-vh', `${Math.round(height)}px`);
      this.style.setProperty('--omri-search-vv-top', `${Math.round(offsetTop)}px`);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('omri-search-drawer', OmriSearchDrawer);
})();
